import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get, v1Post } from '@/lib/api';
import { useDerivedState } from '@/hooks/use-derived-state';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  ArrowLeft, CheckCircle2, Download, FileText, Sparkles,
  Bot, Wand2, RefreshCw, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { CandidatePhotoEditor } from '@/components/CandidatePhotoEditor';
import { UNAUTHORIZED_EDIT_MSG, toastMutationError, ftime } from '@/lib/resumeEnrichmentErrors';
import { SECTION_LABELS, REFINE_MODE_LABELS } from '@/lib/resumeSectionConstants';
import { SectionField } from '@/components/resumeEnrichment/EnrichmentSectionFields';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';

// ===========================================================================
// Candidate Profile card (Sprint 11.5: profile editing lives here now)
// ===========================================================================
export function CandidateProfileCard({
  candidate,
  onPatch,
  saving,
  canEdit = true,
  showDeniedBanner = false,
  evaluationId,
}) {
  // Memoise the remote snapshot so useDerivedState doesn't re-mirror on every
  // render (object identity must be stable — only flip when the underlying
  // server values actually change).
  const remote = useMemo(() => ({
    full_name: candidate.full_name || '',
    email: candidate.email || '',
    phone: candidate.phone || candidate.contact_number || '',
    role_id: candidate.role_id || '',
    department_id: candidate.department_id || '',
  }), [
    candidate.full_name, candidate.email, candidate.phone,
    candidate.contact_number, candidate.role_id, candidate.department_id,
  ]);
  const [local, setLocal] = useDerivedState(remote);
  const [dirty, setDirty] = useState(false);

  const rolesQ = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
    staleTime: 5 * 60_000,
  });
  const deptsQ = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
    staleTime: 5 * 60_000,
  });

  const set = (patch) => { setLocal({ ...local, ...patch }); setDirty(true); };

  const flush = (extraPatch = {}) => {
    const payload = { ...local, ...extraPatch };
    // Only send fields that differ from server.
    const diff = {};
    if (payload.full_name && payload.full_name !== (candidate.full_name || '')) diff.full_name = payload.full_name;
    if (payload.email !== (candidate.email || '')) diff.email = payload.email || null;
    if (payload.phone !== (candidate.phone || candidate.contact_number || '')) diff.phone = payload.phone || null;
    if (payload.role_id && payload.role_id !== (candidate.role_id || '')) diff.role_id = payload.role_id;
    if (payload.department_id && payload.department_id !== (candidate.department_id || '')) diff.department_id = payload.department_id;
    if (Object.keys(diff).length === 0) { setDirty(false); return; }
    if (!canEdit) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      setLocal(remote);
      setDirty(false);
      return;
    }
    onPatch(diff);
    setDirty(false);
  };

  return (
    <Card className="card-elevated" data-testid="candidate-profile-card">
      <CardContent className="space-y-3 p-4">
        {showDeniedBanner && (
          <div
            className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100"
            data-testid="profile-edit-permission-banner"
            role="status"
          >
            <p className="font-semibold">View only</p>
            <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-100/80">
              You don’t have permission to edit this profile. Ask an admin to grant candidate edit access if you need to make changes.
            </p>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <p className="inline-flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" /> Candidate profile
            {dirty && (
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-300">
                {saving ? 'Saving…' : 'Unsaved'}
              </Badge>
            )}
          </p>
          {canEdit && (
            <p className="text-[11px] text-muted-foreground">
              Edits auto-save on blur. These fields appear on the client report header.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div className="md:col-span-2 xl:col-span-5" data-testid="enrichment-photo-section">
            <CandidatePhotoEditor
              candidateId={candidate.id}
              candidateName={candidate.full_name || local.full_name}
              photoUrl={candidate.photo_url}
              size={64}
              evaluationId={evaluationId}
            />
          </div>
          <ProfileField label="Full name" htmlFor="cp-name">
            <Input
              id="cp-name"
              value={local.full_name}
              onChange={(e) => set({ full_name: e.target.value })}
              onBlur={() => flush()}
              readOnly={!canEdit}
              data-testid="profile-full-name"
            />
          </ProfileField>
          <ProfileField label="Email" htmlFor="cp-email">
            <Input
              id="cp-email"
              type="email"
              value={local.email}
              onChange={(e) => set({ email: e.target.value })}
              onBlur={() => flush()}
              readOnly={!canEdit}
              data-testid="profile-email"
            />
          </ProfileField>
          <ProfileField label="Phone" htmlFor="cp-phone">
            <Input
              id="cp-phone"
              value={local.phone}
              onChange={(e) => set({ phone: e.target.value })}
              onBlur={() => flush()}
              readOnly={!canEdit}
              data-testid="profile-phone"
            />
          </ProfileField>
          <ProfileField label="Role">
            <select
              value={local.role_id}
              onChange={(e) => { set({ role_id: e.target.value }); flush({ role_id: e.target.value }); }}
              disabled={!canEdit}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="profile-role"
            >
              <option value="" disabled>Select…</option>
              {(rolesQ.data || []).map((r) => <option key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</option>)}
            </select>
          </ProfileField>
          <ProfileField label="Department">
            <select
              value={local.department_id}
              onChange={(e) => { set({ department_id: e.target.value }); flush({ department_id: e.target.value }); }}
              disabled={!canEdit}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="profile-department"
            >
              <option value="" disabled>Select…</option>
              {(deptsQ.data || []).map((d) => <option key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</option>)}
            </select>
          </ProfileField>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileField({ label, htmlFor, children }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-[11px] font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

// ===========================================================================
// SOURCE pane (left)
// ===========================================================================
export function SourcePane({ candidate, resume }) {
  const [downloading, setDownloading] = useState(false);
  const resumeFile = useMemo(
    () => (candidate.files || []).find((f) => f.kind === 'resume' && f.status === 'uploaded') || null,
    [candidate.files],
  );

  const download = async () => {
    if (!resumeFile || downloading) return;
    setDownloading(true);
    try {
      const { url } = await v1Get(`/candidates/${candidate.id}/files/${resumeFile.id}/download`);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="card-elevated xl:sticky xl:top-4 xl:self-start" data-testid="source-pane">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">Source resume</p>
        </div>

        {resumeFile ? (
          <div className="space-y-3">
            <div className="rounded-xl border bg-secondary/30 p-3">
              <p className="truncate text-sm font-medium" data-testid="source-filename">
                {resumeFile.original_filename}
              </p>
              <p className="text-xs text-muted-foreground">
                {(resumeFile.content_type || '').split('/').pop()?.toUpperCase() || 'FILE'}
                {' · '}Uploaded {ftime(resumeFile.uploaded_at)}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={download}
              disabled={downloading}
              data-testid="open-source-pdf"
            >
              <Download className="h-4 w-4" />
              {downloading ? 'Preparing…' : 'Open original PDF'}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Use the PDF as the source of truth when comparing AI extraction.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed bg-secondary/30 p-4 text-center">
            <p className="text-sm font-medium">No source file on record</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Re-extract mode is disabled for this candidate.
            </p>
          </div>
        )}

        <Separator />

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tips</p>
          <ul className="mt-2 space-y-1.5 text-[12px] leading-relaxed text-muted-foreground">
            <li>• Click any field to edit. Changes auto-save when you click away.</li>
            <li>• Use <strong>Refine with AI</strong> to polish, re-extract or enhance one section at a time.</li>
            <li>• Click <strong>Approve &amp; finish</strong> when done to clear from the queue.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Section editor (right column) — one card per section key
// ===========================================================================
export function SectionEditor({ sectionKey, value, onSave, candidateId, evaluationId, hasSourceFile, canEdit = true }) {
  const [local, setLocal] = useDerivedState(value);
  const [dirty, setDirty] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refinedValue, setRefinedValue] = useState(null);
  const [refinedMode, setRefinedMode] = useState(null);

  const blur = () => {
    if (!dirty) return;
    if (!canEdit) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      setLocal(value);
      setDirty(false);
      return;
    }
    onSave(local);
    setDirty(false);
  };

  const set = (v) => { setLocal(v); setDirty(true); };

  const runRefine = async (mode) => {
    if (refining) return;
    if (!evaluationId) {
      toast.error('evaluation_id is required to refine this section.');
      return;
    }
    if (!canEdit) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    setRefining(true);
    try {
      const res = await v1Post(
        `/candidates/${candidateId}/company-resume/refine-section?evaluation_id=${encodeURIComponent(evaluationId)}`,
        {
          section_key: sectionKey, mode,
        },
      );
      setRefinedValue(res.value);
      setRefinedMode(mode);
      setRefineOpen(true);
    } catch (e) {
      toastMutationError(e, 'AI refine failed');
    } finally {
      setRefining(false);
    }
  };

  const acceptRefinement = () => {
    if (!canEdit) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    setLocal(refinedValue);
    onSave(refinedValue);
    setDirty(false);
    setRefineOpen(false);
    setRefinedValue(null);
    toast.success(`Applied AI ${REFINE_MODE_LABELS[refinedMode]?.label || 'refinement'}.`);
  };

  const empty = isEmpty(local);

  return (
    <Card className="card-elevated" data-testid={`section-card-${sectionKey}`}>
      <CardContent className="space-y-3 p-4">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">{SECTION_LABELS[sectionKey] || sectionKey}</p>
            {empty && (
              <Badge variant="outline" className="border-muted-foreground/30 text-[10px] text-muted-foreground">
                Empty
              </Badge>
            )}
            {dirty && (
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-300">
                Saving…
              </Badge>
            )}
          </div>
          <RefineButton
            sectionKey={sectionKey}
            disabled={!canEdit || refining}
            refining={refining}
            hasSourceFile={hasSourceFile}
            onRefine={runRefine}
          />
        </div>

        {/* Body */}
        <fieldset disabled={!canEdit} className="contents">
          <SectionField
            sectionKey={sectionKey}
            value={local}
            onChange={set}
            onBlur={blur}
            disabled={!canEdit}
          />
        </fieldset>
      </CardContent>

      {/* Refinement preview drawer */}
      <RefinementSheet
        open={refineOpen}
        onOpenChange={setRefineOpen}
        sectionKey={sectionKey}
        mode={refinedMode}
        currentValue={local}
        refinedValue={refinedValue}
        onAccept={acceptRefinement}
      />
    </Card>
  );
}

export function RefineButton({ sectionKey, disabled, refining, hasSourceFile, onRefine }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={disabled}
          data-testid={`refine-button-${sectionKey}`}
        >
          {refining ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5 text-primary" />}
          Refine with AI
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Choose a mode
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onRefine('tighten')} data-testid={`refine-tighten-${sectionKey}`}>
          <Wand2 className="mr-2 h-4 w-4" />
          <div className="flex-1">
            <p className="text-sm font-medium">{REFINE_MODE_LABELS.tighten.label}</p>
            <p className="text-[11px] text-muted-foreground">{REFINE_MODE_LABELS.tighten.hint}</p>
          </div>
        </DropdownMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <DropdownMenuItem
                onClick={() => hasSourceFile && onRefine('re_extract')}
                disabled={!hasSourceFile}
                data-testid={`refine-reextract-${sectionKey}`}
              >
                <FileText className="mr-2 h-4 w-4" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{REFINE_MODE_LABELS.re_extract.label}</p>
                  <p className="text-[11px] text-muted-foreground">{REFINE_MODE_LABELS.re_extract.hint}</p>
                </div>
              </DropdownMenuItem>
            </span>
          </TooltipTrigger>
          {!hasSourceFile && (
            <TooltipContent>Original PDF not on record — re-extract is unavailable.</TooltipContent>
          )}
        </Tooltip>
        <DropdownMenuItem onClick={() => onRefine('enhance')} data-testid={`refine-enhance-${sectionKey}`}>
          <Sparkles className="mr-2 h-4 w-4 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium">{REFINE_MODE_LABELS.enhance.label}</p>
            <p className="text-[11px] text-muted-foreground">{REFINE_MODE_LABELS.enhance.hint}</p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function isEmpty(v) {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

// ===========================================================================
// Refinement preview Sheet (current vs AI suggestion)
// ===========================================================================
export function RefinementSheet({ open, onOpenChange, sectionKey, mode, currentValue, refinedValue, onAccept }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-[720px] overflow-y-auto sm:max-w-[720px]"
        data-testid={`refine-preview-sheet-${sectionKey || ''}`}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            AI {REFINE_MODE_LABELS[mode]?.label || 'refinement'} — {SECTION_LABELS[sectionKey] || sectionKey}
          </SheetTitle>
          <SheetDescription>
            Compare the current value (left) with the AI suggestion (right). Accept to apply, or close to discard.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current</p>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border bg-secondary/30 p-3 text-[12px] leading-relaxed">
              {prettyValue(currentValue)}
            </pre>
          </div>
          <div>
            <p className="mb-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3 w-3" /> AI suggestion
            </p>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-primary/40 bg-primary/5 p-3 text-[12px] leading-relaxed">
              {prettyValue(refinedValue)}
            </pre>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="refine-reject">
            Discard
          </Button>
          <Button size="sm" className="gap-1.5" onClick={onAccept} data-testid="refine-accept">
            <CheckCircle2 className="h-4 w-4" /> Accept &amp; replace
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function prettyValue(v) {
  if (v == null) return '(empty)';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); }
  catch { return String(v); }
}

// ===========================================================================
// Approve button (confirm dialog)
// ===========================================================================
export function ApproveButton({ onConfirm, pending, isApproved, canEdit = true }) {
  const triggerConfirm = () => {
    if (!canEdit) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    onConfirm();
  };

  if (isApproved) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={!canEdit}
            data-testid="approve-again-button"
          >
            <CheckCircle2 className="h-4 w-4" /> Re-approve
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-approve this resume?</AlertDialogTitle>
            <AlertDialogDescription>
              Already marked as approved. Re-approving refreshes the edited timestamp and returns you to the queue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={triggerConfirm} disabled={pending || !canEdit}>
              {pending ? 'Approving…' : 'Yes, re-approve'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={!canEdit}
          data-testid="approve-finish-button"
        >
          <CheckCircle2 className="h-4 w-4" /> Approve &amp; finish
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve this resume and finish?</AlertDialogTitle>
          <AlertDialogDescription>
            Marks the standardised resume as reviewed by HR and removes it from the &ldquo;Needs review&rdquo;
            queue. You can edit it again later from the candidate&apos;s Resume tab.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="approve-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={triggerConfirm} disabled={pending || !canEdit} data-testid="approve-confirm">
            {pending ? 'Approving…' : 'Approve & finish'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ===========================================================================
// Loading / Error states
// ===========================================================================
export function WorkspaceSkeleton() {
  return (
    <div className="space-y-4" data-testid="workspace-loading">
      <Skeleton className="h-16 w-full rounded-xl" />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,_340px)_minmax(0,_1fr)]">
        <Skeleton className="h-[360px] w-full rounded-xl" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
        </div>
      </div>
    </div>
  );
}

export function ErrorState({ title, message, onBack, candidate }) {
  return (
    <div className="space-y-4" data-testid="workspace-error">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5" data-testid="back-to-queue">
        <ArrowLeft className="h-4 w-4" /> Back to queue
      </Button>
      <Card className="card-elevated">
        <CardContent className="space-y-2 p-10">
          <p className="text-base font-semibold">{title}</p>
          {message && <p className="text-sm text-muted-foreground">{String(message)}</p>}
          {candidate && (
            <p className="text-xs text-muted-foreground">
              Candidate: <strong>{candidate.full_name}</strong>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
